import { computed, ref, watch, type Ref } from 'vue'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import {
  useFormatPreview,
  type FormatPreviewProps
} from '@/composables/format-edit/useFormatPreview'
import { usePreviewOverflow } from '@/composables/format-edit/usePreviewOverflow'
import { formatPreviewCreateDate } from '@/utils/format-edit/previewDates'
import {
  buildEduJobHistoryTable,
  buildEduJobHistoryTable4New,
  buildLicenseHistory4NewTable,
  buildLicenseHistoryTable
} from '@/utils/format-edit/rirekishoPreviewTables'

export type RirekishoPreviewSource = FormatPreviewProps

export type RirekishoPreviewOptions = {
  maxEduJobRows?: number
  maxLicenseRows?: number
  maxLicense4NewRows?: number
  textareaMaxHeights: Record<string, number>
  overflowFields?: string[]
  extraWatchSources?: Array<{ name: string; source: () => unknown }>
  useEduJobTable4New?: boolean
  useLicenseTable4New?: boolean
  optionItems?: { commuteDescription: boolean }
  textMaxLength?: { commuteDescription: number }
  revalidateOnFormatDataChange?: boolean
}

const DEFAULT_OVERFLOW_FIELDS = [
  'reason',
  'reasonNew',
  'request',
  'addressFurigana'
]

function compactJoin(parts: Array<string | undefined | null>): string {
  return parts.filter(Boolean).join('-')
}

export function useRirekishoPreview(
  source: RirekishoPreviewSource,
  options: RirekishoPreviewOptions
) {
  const { formLib } = useHostServices()
  const formatPreview = useFormatPreview(source)

  const maxEduJobRows = options.maxEduJobRows ?? 22
  const maxLicenseRows = options.maxLicenseRows ?? 6
  const maxLicense4NewRows = options.maxLicense4NewRows ?? 8
  const optionItems = options.optionItems ?? { commuteDescription: true }
  const textMaxLength = options.textMaxLength ?? { commuteDescription: 84 }

  const overflowFieldNames = Array.from(
    new Set([
      ...(options.overflowFields ?? DEFAULT_OVERFLOW_FIELDS),
      ...Object.keys(options.textareaMaxHeights)
    ])
  )
  const overflowEls: Record<string, Ref<HTMLElement | null>> = {}
  for (const name of overflowFieldNames) {
    overflowEls[name] = ref(null)
  }

  const overflow = usePreviewOverflow({
    getEl: (name) => overflowEls[name]?.value ?? undefined,
    maxHeights: options.textareaMaxHeights,
    watchSources: [
      { name: 'reason', source: () => source.formatData?.reason },
      { name: 'reasonNew', source: () => source.formatData?.reasonNew },
      { name: 'request', source: () => source.formatData?.request },
      {
        name: 'addressFurigana',
        source: () => source.basicData?.addressFurigana
      },
      ...Object.keys(options.textareaMaxHeights)
        .filter(
          (name) =>
            !['reason', 'reasonNew', 'request', 'addressFurigana'].includes(
              name
            )
        )
        .map((name) => ({
          name,
          source: () => source.formatData?.[name]
        })),
      ...(options.extraWatchSources ?? [])
    ]
  })

  function checkValid() {
    for (const name of overflowFieldNames) {
      overflow.adjustFontSize(name)
    }
  }

  if (options.revalidateOnFormatDataChange) {
    watch(
      () => source.formatData,
      () => {
        checkValid()
      }
    )
  }

  const tel = computed(() =>
    compactJoin([
      source.basicData?.tel1,
      source.basicData?.tel2,
      source.basicData?.tel3
    ])
  )

  const contactTel = computed(() =>
    compactJoin([
      source.basicData?.contactTel1,
      source.basicData?.contactTel2,
      source.basicData?.contactTel3
    ])
  )

  const age = computed(() => source.basicData?.age)

  const formatedCreateDate = computed(() =>
    formatPreviewCreateDate(
      source.formatData?.createDate,
      formLib.dateFormat,
      'today'
    )
  )

  const isMale = computed(() => source.basicData?.sex === '1')
  const isFemale = computed(() => source.basicData?.sex === '2')
  const isGenderDisplay = computed(() => source.basicData?.sex === '-1')

  const eduJobHistoryTable4NewResult = computed(() =>
    buildEduJobHistoryTable4New(source.formatData, maxEduJobRows)
  )
  const eduJobHistoryTableResult = computed(() =>
    buildEduJobHistoryTable(source.formatData, maxEduJobRows)
  )
  const licenseHistory4NewTableResult = computed(() =>
    buildLicenseHistory4NewTable(source.formatData, maxLicense4NewRows)
  )
  const licenseHistoryTableResult = computed(() =>
    buildLicenseHistoryTable(source.formatData, maxLicenseRows)
  )

  const eduJobHistoryTable4New = computed(
    () => eduJobHistoryTable4NewResult.value.rows
  )
  const eduJobHistoryTable = computed(() => eduJobHistoryTableResult.value.rows)
  const licenseHistory4NewTable = computed(
    () => licenseHistory4NewTableResult.value.rows
  )
  const licenseHistoryTable = computed(
    () => licenseHistoryTableResult.value.rows
  )

  const errorMessages = computed(() => ({
    ...overflow.errorMessages,
    eduJobHistory: options.useEduJobTable4New
      ? eduJobHistoryTable4NewResult.value.error
      : eduJobHistoryTableResult.value.error,
    licenseHistory: options.useLicenseTable4New
      ? licenseHistory4NewTableResult.value.error
      : licenseHistoryTableResult.value.error
  }))

  return {
    formLib,
    ...formatPreview,
    ...overflowEls,
    checkValid,
    classObject: overflow.classObject,
    errorMessages,
    adjustFontSize: overflow.adjustFontSize,
    tel,
    contactTel,
    age,
    formatedCreateDate,
    isMale,
    isFemale,
    isGenderDisplay,
    eduJobHistoryTable,
    eduJobHistoryTable4New,
    licenseHistoryTable,
    licenseHistory4NewTable,
    optionItems,
    textMaxLength
  }
}
